import { useEffect } from 'react';

import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { Container, CssBaseline, ThemeProvider } from '@mui/material';

import App from './App';
import { useApp } from './context';
import { DateLocalizationProvider } from './services/DateLocalizationProvider';
import stylisPluginExtraScope from './stylisPluginExtraScope';
import { rootElement } from './theme';

const AppInit = (): JSX.Element => {
    const { loading, error, theme, contentView } = useApp();

    // Create emotion cache
    const cache = createCache({
        key: 'css',
        stylisPlugins: [stylisPluginExtraScope('#eventilla-calendar')],
    });

    if (error) {
        console.error(error?.status_message ?? error);
        return <Container>404</Container>;
    }

    if (loading) {
        return null;
    }

    return (
        <CacheProvider value={cache}>
            <ThemeProvider theme={theme}>
                <DateLocalizationProvider>
                    <RootAriaLabelMuiBugFix />
                    <CssBaseline />
                    <App contentView={contentView} />
                    <div id='menu-portal-container' />
                </DateLocalizationProvider>
            </ThemeProvider>
        </CacheProvider>
    );
};

// see https://github.com/mui/material-ui/issues/19450#issuecomment-1310163344
function RootAriaLabelMuiBugFix(): null {
    useEffect(() => {
        // RootAriaLabelMuiBugFix couldn't find the element.
        if (!rootElement) {
            return;
        }

        const observer = new MutationObserver(() => {
            if (rootElement.getAttribute('aria-hidden')) {
                rootElement.removeAttribute('aria-hidden');
            }
        });

        observer.observe(rootElement, {
            attributeFilter: ['aria-hidden'],
        });

        return () => {
            observer.disconnect();
        };
    }, [rootElement]);

    return null;
}

export default AppInit;
